import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Calendar, Clock, MapPin, Phone,
  Users, FileText, CheckCircle, XCircle, AlertTriangle,
  MessageSquare, Heart, User, ChevronDown, ChevronUp,
  Plus, Trash2,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  providerDashboardService,
  type ProviderAppointmentDetail,
} from '@lib/supabase/providerDashboard.service'
import { appointmentService, type VisitResult } from '@lib/supabase/appointment.service'
import { sendClinicalResultReadyEmail } from '@lib/supabase/email.service'
import Button from '@components/web/Button'
import { useProviderAuth } from '@hooks/useProviderAuth'
import { queryKeys } from '@lib/queryKeys'

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, string> = {
  requested: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  scheduled: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  completed: 'bg-blue-50 text-blue-800 border-blue-200',
  cancelled: 'bg-red-50 text-red-800 border-red-200',
}

const URGENCY: Record<string, { label: string; color: string }> = {
  normal: { label: 'Normal', color: 'text-emerald-600' },
  high:   { label: 'High',   color: 'text-orange-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', maximumFractionDigits: 0,
  }).format(n)
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  })
}

function formatTime12h(t?: string) {
  if (!t) return '—'
  const [h, m] = t.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
}

function normalizeStatus(status?: string | null) {
  return (status || '').toLowerCase().trim()
}

// ── Detail Row ────────────────────────────────────────────────────────────────

function DetailRow({ icon: Icon, label, value, valueClass }: {
  icon: React.ElementType
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={15} className="text-muted-foreground" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={clsx('text-sm font-medium text-foreground mt-0.5', valueClass)}>{value}</p>
      </div>
    </div>
  )
}

// ── Section wrapper ───────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border overflow-hidden">
      <div className="px-5 py-3.5 border-b border-border">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      </div>
      <div className="px-5 py-1">{children}</div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ProviderAppointmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { provider } = useProviderAuth()
  const queryClient = useQueryClient()

  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [actionError, setActionError]     = useState<string | null>(null)
  const [showMedInfo, setShowMedInfo]     = useState(false)
  const [showVisitNoteModal, setShowVisitNoteModal] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [diagnosisText, setDiagnosisText] = useState('')
  const [prescriptions, setPrescriptions] = useState<Array<{ drugName: string; dosageInstructions: string }>>([])
  const [providerSignature, setProviderSignature] = useState('')
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [visitNoteError, setVisitNoteError] = useState<string | null>(null)

  const detailQuery = useQuery({
    queryKey: queryKeys.provider.appointmentDetail(provider?.id, id),
    enabled: !!provider?.id && !!id,
    queryFn: () => providerDashboardService.getProviderAppointmentById(id!),
  })

  const appt: ProviderAppointmentDetail | null = detailQuery.data ?? null
  const isLoading = detailQuery.isPending
  const visitResultQuery = useQuery({
    queryKey: [...queryKeys.provider.appointmentDetail(provider?.id, id), 'visit-note'],
    enabled: !!appt?.id,
    queryFn: () => appointmentService.getVisitResult(appt!.id),
  })
  const visitResult: VisitResult | null = visitResultQuery.data ?? null

  const handleAction = async (status: string, reason?: string) => {
    if (!appt) return
    setActionLoading(status)
    setActionError(null)
    const result = await providerDashboardService.updateStatus(appt.id, status, reason)
    setActionLoading(null)
    if (result.success) {
      queryClient.setQueryData(queryKeys.provider.appointmentDetail(provider?.id, appt.id), (current: ProviderAppointmentDetail | null | undefined) =>
        current ? { ...current, status } : current
      )
      await queryClient.invalidateQueries({ queryKey: queryKeys.provider.appointmentPages(provider?.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.provider.upcomingAppointments(provider?.id) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.provider.stats(provider?.id) })
      await detailQuery.refetch()
    } else {
      setActionError(result.error ?? 'Action failed.')
    }
  }

  const handleOpenVisitNote = () => {
    setNoteText('')
    setDiagnosisText(visitResult?.diagnosisText ?? '')
    setPrescriptions(visitResult?.prescriptions.map((prescription) => ({
      drugName: prescription.drugName,
      dosageInstructions: prescription.dosageInstructions,
    })) ?? [])
    setProviderSignature(visitResult?.providerSignature ?? provider?.name ?? '')
    setVisitNoteError(null)
    setShowVisitNoteModal(true)
  }

  const handleSaveVisitNote = async () => {
    if (!appt) return

    const trimmedNote = noteText.trim()
    const trimmedDiagnosis = diagnosisText.trim()
    const trimmedSignature = providerSignature.trim()

    if (!trimmedNote || !trimmedDiagnosis || !trimmedSignature) {
      setVisitNoteError('Please add the visit note, diagnosis, and signature.')
      return
    }

    setIsSavingNote(true)
    setVisitNoteError(null)

    const result = await appointmentService.saveVisitResult({
      appointmentId: appt.id,
      providerId: appt.providerId,
      patientId: appt.patientId,
      noteText: trimmedNote,
      diagnosisText: trimmedDiagnosis,
      prescriptions,
      providerSignature: trimmedSignature,
    })

    setIsSavingNote(false)

    if (result.success && result.result) {
      queryClient.setQueryData([...queryKeys.provider.appointmentDetail(provider?.id, id), 'visit-note'], result.result)
      setShowVisitNoteModal(false)

      if (appt.patientEmail) {
        await sendClinicalResultReadyEmail({
          email: appt.patientEmail,
          patientName: appt.patientName,
          providerName: provider?.name ?? 'Your healthcare provider',
          serviceType: appt.serviceType,
          date: appt.scheduledDate,
          appointmentId: appt.id,
        })
      }

      return
    }

    setVisitNoteError(result.error ?? 'Failed to save documentation. Please try again.')
  }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 animate-pulse">
        <div className="h-5 w-24 bg-muted rounded" />
        <div className="h-28 bg-muted" />
        <div className="h-52 bg-muted" />
        <div className="h-36 bg-muted" />
      </div>
    )
  }

  if (!appt) {
    return (
      <div className="p-4 sm:p-6 text-center py-20">
        <p className="text-muted-foreground">Appointment not found.</p>
        <Button className="mt-4" onClick={() => navigate('/provider/appointments')}>Back to Appointments</Button>
      </div>
    )
  }

  const normalizedStatus = normalizeStatus(appt.status)
  const isActive = normalizedStatus === 'requested' || normalizedStatus === 'scheduled'
  const isChat   = appt.serviceType === 'Chat Consultation'
  const hasMed   = appt.medicalInfo && Object.values(appt.medicalInfo).some(Boolean)

  return (
    <div className="p-4 sm:p-6 space-y-5 pb-8">
      {/* Back */}
      <button
        onClick={() => navigate('/provider/appointments')}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft size={16} /> Appointments
      </button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">Appointment Details</h1>
        <span className={clsx(
          'text-xs px-2.5 py-1 rounded-full border font-medium',
          STATUS_STYLE[normalizedStatus] ?? 'bg-muted text-muted-foreground border-border'
        )}>
          {appt.status}
        </span>
      </div>

      {/* Error banner */}
      {actionError && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
          <AlertTriangle size={14} /> {actionError}
          <button onClick={() => setActionError(null)} className="ml-auto text-xs underline">Dismiss</button>
        </div>
      )}

      {/* Patient Information */}
      <Section title="Patient Information">
        <div className="py-3 flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
            {appt.patientImage ? (
              <img src={appt.patientImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={20} className="text-primary" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground">{appt.patientName}</p>
            {(appt.patientAge || appt.patientGender) && (
              <p className="text-sm text-muted-foreground mt-0.5">
                {[appt.patientAge ? `${appt.patientAge} yrs` : null, appt.patientGender].filter(Boolean).join(' • ')}
              </p>
            )}
            {isActive && appt.patientPhone && (
              <a
                href={`tel:${appt.patientPhone}`}
                className="inline-flex items-center gap-1.5 mt-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <Phone size={13} /> {appt.patientPhone}
              </a>
            )}
          </div>
        </div>
      </Section>

      {/* Appointment Information */}
      <Section title="Appointment Information">
        <DetailRow icon={FileText} label="Service" value={appt.serviceType} />
        <DetailRow
          icon={Calendar}
          label="Duration"
          value={appt.duration === 'daily'
            ? `Daily Care${appt.numberOfDays ? ` (${appt.numberOfDays} days)` : ''}`
            : 'Single Visit'}
        />
        {appt.numberOfPatients && (
          <DetailRow icon={Users} label="Number of Patients" value={String(appt.numberOfPatients)} />
        )}
        {appt.urgencyLevel && (
          <DetailRow
            icon={AlertTriangle}
            label="Urgency Level"
            value={URGENCY[appt.urgencyLevel]?.label ?? appt.urgencyLevel}
            valueClass={URGENCY[appt.urgencyLevel]?.color}
          />
        )}
        <DetailRow
          icon={Calendar}
          label="Date"
          value={formatDate(appt.scheduledDate)}
        />
        <DetailRow
          icon={Clock}
          label="Time"
          value={formatTime12h(appt.scheduledTime)}
        />
        <DetailRow
          icon={MapPin}
          label="Location"
          value={appt.location || '—'}
        />
      </Section>

      {/* Medical Information */}
      {hasMed && (
        <div className="bg-card border border-border overflow-hidden">
          <button
            onClick={() => setShowMedInfo(!showMedInfo)}
            className="w-full flex items-center justify-between px-5 py-3.5"
          >
            <h2 className="text-sm font-semibold text-foreground">Medical Information</h2>
            {showMedInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {showMedInfo && (
            <div className="px-5 pb-5 space-y-4 border-t border-border pt-4">
              {appt.medicalInfo?.currentSymptoms && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Heart size={14} className="text-red-500" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Current Symptoms</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.currentSymptoms}</p>
                </div>
              )}
              {appt.medicalInfo?.allergies && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={14} className="text-amber-500" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Allergies</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.allergies}</p>
                </div>
              )}
              {appt.medicalInfo?.currentMedications && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-emerald-600" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Current Medications</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.currentMedications}</p>
                </div>
              )}
              {appt.medicalInfo?.medicalHistory && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-muted-foreground" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Medical History</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.medicalHistory}</p>
                </div>
              )}
              {appt.medicalInfo?.surgeryHistory && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-blue-500" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Surgery History</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.surgeryHistory}</p>
                </div>
              )}
              {appt.medicalInfo?.familyHistory && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Users size={14} className="text-violet-500" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Family Medical History</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.familyHistory}</p>
                </div>
              )}
              {appt.medicalInfo?.accidentRecord && (
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle size={14} className="text-orange-500" />
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Accident Record</p>
                  </div>
                  <p className="text-sm text-foreground">{appt.medicalInfo.accidentRecord}</p>
                </div>
              )}
              {(appt.medicalInfo?.bloodType || appt.medicalInfo?.heightCm || appt.medicalInfo?.weightKg) && (
                <div className="grid grid-cols-3 gap-3">
                  {appt.medicalInfo?.bloodType && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Blood Type</p>
                      <p className="text-sm font-medium text-foreground">{appt.medicalInfo.bloodType}</p>
                    </div>
                  )}
                  {appt.medicalInfo?.heightCm && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Height</p>
                      <p className="text-sm font-medium text-foreground">{appt.medicalInfo.heightCm} cm</p>
                    </div>
                  )}
                  {appt.medicalInfo?.weightKg && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Weight</p>
                      <p className="text-sm font-medium text-foreground">{appt.medicalInfo.weightKg} kg</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Emergency Contacts */}
      {isActive && appt.emergencyContacts && appt.emergencyContacts.length > 0 && (
        <Section title="Emergency Contacts">
          {appt.emergencyContacts.map((contact) => (
            <div key={contact.id} className="flex items-center justify-between py-3 border-b border-border last:border-0">
              <div>
                <p className="text-sm font-medium text-foreground">{contact.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{contact.relationship} · {contact.phoneNumber}</p>
              </div>
              <a
                href={`tel:${contact.phoneNumber}`}
                title={`Call ${contact.name}`}
                className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0 text-emerald-600 hover:bg-emerald-100"
              >
                <Phone size={14} />
              </a>
            </div>
          ))}
        </Section>
      )}

      {/* Additional Notes */}
      {appt.notes && (
        <div className="bg-card border border-border p-5 rounded-none">
          <h2 className="text-sm font-semibold text-foreground mb-2">Additional Notes</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{appt.notes}</p>
        </div>
      )}

      {/* Cost */}
      <div className="bg-card border border-border p-5 flex items-center justify-between rounded-none">
        <span className="text-sm text-muted-foreground">Consultation Fee</span>
        <span className="text-base font-bold text-foreground">{formatCurrency(appt.totalCost)}</span>
      </div>

      {visitResult && (
        <div className="bg-card border border-border p-5 rounded-none">
          <h2 className="text-sm font-semibold text-foreground mb-2">Clinical Result</h2>
          <p className="text-xs font-medium text-muted-foreground">Diagnosis</p>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{visitResult.diagnosisText}</p>
          {visitResult.prescriptions.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Prescription</p>
              {visitResult.prescriptions.map((prescription) => (
                <div key={prescription.id} className="rounded-none bg-muted/50 px-4 py-3">
                  <p className="text-sm font-medium text-foreground">{prescription.drugName}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{prescription.dosageInstructions}</p>
                </div>
              ))}
            </div>
          )}
          {visitResult.providerSignature && (
            <div className="mt-4 rounded-none bg-muted/50 px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground">Signed by</p>
              <p className="mt-0.5 text-sm font-medium text-foreground">{visitResult.providerSignature}</p>
            </div>
          )}
          {visitResult.createdAt && (
            <p className="mt-3 text-xs text-muted-foreground">
              {new Date(visitResult.createdAt).toLocaleString()}
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      {normalizedStatus === 'requested' && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 text-destructive border-destructive/30 hover:bg-destructive/5"
              isLoading={actionLoading === 'Cancelled'}
              disabled={!!actionLoading}
              onClick={() => {
                const reason = window.prompt('Reason for declining (optional):') ?? undefined
                handleAction('Cancelled', reason)
              }}
            >
              <XCircle size={15} className="mr-1.5" /> Decline
            </Button>
            <Button
              className="flex-1"
              isLoading={actionLoading === 'Scheduled'}
              disabled={!!actionLoading}
              onClick={() => handleAction('Scheduled')}
            >
              <CheckCircle size={15} className="mr-1.5" /> Accept
            </Button>
          </div>
          {isChat && (
            <Button
              variant="outline"
              onClick={() => navigate(`/provider/chat/${appt.id}`, { state: { backPath: `/provider/appointments/${appt.id}` } })}
            >
              <MessageSquare size={15} className="mr-1.5" /> Open Chat
            </Button>
          )}
        </div>
      )}

      {(normalizedStatus === 'scheduled' || normalizedStatus === 'completed') && !visitResult && (
        <div className="flex flex-col gap-2">
          {isChat && (
            <Button onClick={() => navigate(`/provider/chat/${appt.id}`, { state: { backPath: `/provider/appointments/${appt.id}` } })}>
              <MessageSquare size={15} className="mr-1.5" /> Open Chat
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleOpenVisitNote}
          >
            <FileText size={15} className="mr-1.5" /> Document Appointment
          </Button>
        </div>
      )}

      {showVisitNoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-none border border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div>
                <h2 className="text-base font-semibold text-foreground">Appointment Documentation</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">Record private findings.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowVisitNoteModal(false)}
                className="text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>

            <div className="min-h-0 space-y-4 overflow-y-auto px-5 py-4">
              <div>
                <label htmlFor="visit-note" className="mb-1.5 block text-sm font-medium text-foreground">Visit Note</label>
                <textarea
                  id="visit-note"
                  value={noteText}
                  onChange={(event) => setNoteText(event.target.value)}
                  rows={4}
                  placeholder="Document the visit summary, observations, and actions taken."
                  className="w-full rounded-lg border border-border bg-slate-50 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              <div>
                <label htmlFor="visit-diagnosis" className="mb-1.5 block text-sm font-medium text-foreground">Diagnosis</label>
                <textarea
                  id="visit-diagnosis"
                  value={diagnosisText}
                  onChange={(event) => setDiagnosisText(event.target.value)}
                  rows={2}
                  placeholder="Record the diagnosis for the patient."
                  className="w-full rounded-lg border border-border bg-slate-50 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-sm font-medium text-foreground">Prescription</label>
                  <button
                    type="button"
                    onClick={() => setPrescriptions((current) => [...current, { drugName: '', dosageInstructions: '' }])}
                    className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                  >
                    <Plus size={15} /> Add drug
                  </button>
                </div>
                <div className="space-y-2">
                  {prescriptions.length === 0 && (
                    <p className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">No drugs added.</p>
                  )}
                  {prescriptions.map((prescription, index) => (
                    <div key={`prescription-${index}`} className="rounded-lg border border-border p-2.5">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold text-muted-foreground">Drug {index + 1}</p>
                        <button
                          type="button"
                          title="Remove drug"
                          aria-label={`Remove drug ${index + 1}`}
                          onClick={() => setPrescriptions((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                        <input
                          value={prescription.drugName}
                          onChange={(event) => setPrescriptions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, drugName: event.target.value } : item))}
                          placeholder="Drug name"
                          className="w-full rounded-lg border border-border bg-slate-50 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <textarea
                          value={prescription.dosageInstructions}
                          onChange={(event) => setPrescriptions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, dosageInstructions: event.target.value } : item))}
                          rows={1}
                          placeholder="Dosage and instructions"
                          className="w-full resize-none rounded-lg border border-border bg-slate-50 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="provider-signature" className="mb-1.5 block text-sm font-medium text-foreground">Provider Signature</label>
                <input
                  id="provider-signature"
                  type="text"
                  value={providerSignature}
                  onChange={(event) => setProviderSignature(event.target.value)}
                  placeholder="Enter your full name"
                  className="w-full rounded-lg border border-border bg-slate-50 px-3 py-2 text-sm text-foreground placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {visitNoteError && (
                <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {visitNoteError}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowVisitNoteModal(false)}
                  disabled={isSavingNote}
                >
                  Cancel
                </Button>
                <Button
                  isLoading={isSavingNote}
                  onClick={handleSaveVisitNote}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
